import { mergeAttributes, Node } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import { type NodeViewProps } from '@tiptap/react'
import { InputRule } from '@tiptap/core'
import { Plugin, PluginKey, type EditorState } from '@tiptap/pm/state'
import { wrapTemplateVariables } from '../utils'

const TemplateVariableView = ({ node, selected, deleteNode, editor, getPos }: NodeViewProps) => {
    // Reflect the marks applied to this atom node onto the rendered chip. Uses
    // dedicated CSS classes (with !important in tiptap.css) rather than inline
    // styles because global stylesheets in some hosts (MUI/Joy UI on admin,
    // Tailwind utilities elsewhere) can have higher specificity than a class
    // on the inner span and would otherwise override an inline style.
    const marks = node.marks || []
    const has = (name: string) => marks.some((m) => m.type.name === name)
    const markClasses = [has('bold') && 'tv-bold', has('italic') && 'tv-italic', has('underline') && 'tv-underline', has('strike') && 'tv-strike']
        .filter(Boolean)
        .join(' ')

    // Clicking a chip places the CARET instead of node-selecting: left half → before the chip, right
    // half → after. Without this, a click "just before" a chip either selected the whole chip or (in
    // some hosts) left the editor unfocused entirely — typing next to a variable felt impossible.
    const placeCaret = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button')) return // ✕ keeps its delete behaviour
        e.preventDefault()
        e.stopPropagation()
        const pos = getPos()
        if (typeof pos !== 'number') return
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        const target = e.clientX < rect.left + rect.width / 2 ? pos : pos + node.nodeSize
        // Defer past the click's own event processing — focusing synchronously inside mousedown gets
        // undone before the gesture completes (observed: isFocused false right after a successful run).
        window.setTimeout(() => editor.chain().focus().setTextSelection(target).run(), 0)
    }

    return (
        <NodeViewWrapper as="span" className="template-variable-wrapper" contentEditable={false} onMouseDown={placeCaret}>
            <span
                title={`{{${node.attrs.variableName}}}`}
                className={`tv-chip inline-flex cursor-text select-none items-center rounded-xl bg-success-soft px-1.5 py-0.5 text-xs text-success-soft-foreground ${selected ? 'ring-2 ring-success' : ''} ${markClasses}`}
                contentEditable={false}
            >
                {`{{${node.attrs.variableName}}}`}
                <button
                    type="button"
                    aria-label={`Remove {{${node.attrs.variableName}}}`}
                    className="ml-1 cursor-pointer text-xs leading-none text-success-soft-foreground opacity-60 hover:opacity-100"
                    onClick={deleteNode}
                    contentEditable={false}
                >
                    &times;
                </button>
            </span>
        </NodeViewWrapper>
    )
}

export const TemplateVariableNode = Node.create({
    name: 'templateVariable',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,

    addAttributes() {
        return {
            variableName: {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute('data-variable'),
                renderHTML: (attributes: Record<string, string>) => ({ 'data-variable': attributes.variableName }),
            },
        }
    },

    parseHTML() {
        return [{ tag: 'span[data-variable]' }]
    },

    renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, string> }) {
        return ['span', mergeAttributes(HTMLAttributes, { 'data-variable': HTMLAttributes['data-variable'] }), `{{${HTMLAttributes['data-variable']}}}`]
    },

    addNodeView() {
        return ReactNodeViewRenderer(TemplateVariableView)
    },

    addInputRules() {
        return [
            new InputRule({
                find: /\{\{([a-zA-Z_]+)\}\}$/,
                handler: ({ state, range, match }: { state: EditorState; range: { from: number; to: number }; match: RegExpMatchArray }) => {
                    const variableName = match[1]
                    const { tr } = state
                    const node = this.type.create({ variableName })
                    tr.replaceWith(range.from, range.to, node)
                },
            }),
        ]
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('templateVariablePaste'),
                props: {
                    // Use the structural helper so pasted content that already
                    // contains <span data-variable> isn't re-wrapped.
                    transformPastedHTML(html) {
                        return wrapTemplateVariables(html)
                    },
                },
            }),
        ]
    },
})
